import React from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { NamedLink } from '../../components';
import { isFavorite } from '../../util/favorites';
import { toggleFavoriteListing } from '../../ducks/user.duck';

import css from './FavoriteButton.module.css';

/**
 * The heart beside the share control.
 *
 * Logged out there is nowhere to save to, so the heart becomes a link to login
 * rather than a control that silently does nothing. The saved state itself
 * lives on currentUser, so it survives a reload and follows the user between
 * devices without any local storage.
 *
 * Path from Figma 443:2363 — outline by default, filled when saved.
 */

/** Figma heart path (20×20 frame). */
const HEART_PATH =
  'M8.72856 16.5615C6.47154 14.8241 2 10.8521 2 7.27774C2 4.91521 3.68421 3 6 3C7.2 3 8.4 3.41176 10 5.05881C11.6 3.41176 12.8 3 14 3C16.3158 3 18 4.91521 18 7.27774C18 10.8521 13.5285 14.8241 11.2714 16.5615C10.5119 17.1462 9.48808 17.1462 8.72856 16.5615Z';

const HeartIcon = ({ filled }) => (
  <svg
    className={classNames(css.icon, { [css.iconFilled]: filled })}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d={HEART_PATH}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * @component
 * @param {Object} props
 * @param {string} props.listingId uuid of the listing being viewed
 * @param {string?} props.className extra style rules
 * @returns {JSX.Element}
 */
const FavoriteButton = props => {
  const { listingId, className } = props;
  const intl = useIntl();
  const dispatch = useDispatch();

  const currentUser = useSelector(state => state.user.currentUser);
  const inProgress = useSelector(state => state.user.toggleFavoriteInProgress);
  const saved = isFavorite(currentUser, listingId);

  const label = intl.formatMessage({
    id: saved ? 'ListingPage.favoriteRemove' : 'ListingPage.favoriteAdd',
  });

  if (!currentUser) {
    return (
      <NamedLink
        name="LoginPage"
        className={classNames(css.root, className)}
        title={intl.formatMessage({ id: 'ListingPage.favoriteSignedOut' })}
      >
        <HeartIcon filled={false} />
        <span className={css.visuallyHidden}>
          <FormattedMessage id="ListingPage.favoriteSignedOut" />
        </span>
      </NamedLink>
    );
  }

  return (
    <button
      type="button"
      className={classNames(css.root, { [css.saved]: saved }, className)}
      onClick={() => dispatch(toggleFavoriteListing(listingId))}
      disabled={inProgress}
      aria-pressed={saved}
      title={label}
    >
      <HeartIcon filled={saved} />
      <span className={css.visuallyHidden}>{label}</span>
    </button>
  );
};

export default FavoriteButton;
